import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentLang } from '../../i18n/i18n';
import { formatCurrency } from '../../utils';
import { amountText, grossFromNet, netFromGross } from '../../utils/netGross';
import { usePriceEntryMode } from '../../hooks/usePriceEntry';
import type { PriceTaxRate } from '../../utils/priceTaxRate';
import { formatRatePercent } from '../tax/formatRatePercent';

export interface PriceInput {
    /** What the input shows: the net amount when entering net, else the gross. */
    value: string;
    onChange: (text: string) => void;
    /** True when the typed amount is net. */
    entersNet: boolean;
    /** The field's label, marked "netto" when the typed amount is net. */
    label: (base: string) => string;
}

interface Props {
    /** The stored gross price, as the surrounding form holds it. */
    gross: string;
    /** Receives the gross price to keep: converted from the typed net, or as typed. */
    onGrossChange: (gross: string) => void;
    /** The VAT rate this price is charged at (`useTaxRateOf`). */
    rate: PriceTaxRate;
    /** Renders the surface's own input, so each keeps its look. */
    children: (input: PriceInput) => ReactNode;
}

const isAmount = (text: string): boolean => text.trim() !== '' && Number.isFinite(Number(text));

/** Typed text that is not an amount is passed through, so the form's own validation sees it. */
const convert = (text: string, toOther: (amount: number) => number): string =>
    (isAmount(text) ? amountText(toOther(Number(text))) : text);

const sameAmount = (a: string, b: string | null): boolean =>
    b !== null && (a === b || (isAmount(a) && isAmount(b) && Number(a) === Number(b)));

/**
 * A price field that takes net amounts when the tenant enters prices net
 * (#2967, docs/plans/net-price-entry.md). The form keeps holding GROSS — what
 * is stored and what the customer pays; this field converts on the way in and
 * out (`utils/netGross.ts`) and shows the gross live beside the typed net.
 *
 * Without a single VAT rate the field stays a gross field and says why: net
 * entry needs a rate to convert at, and guessing one would store a wrong price.
 */
const NetGrossField = ({ gross, onGrossChange, rate, children }: Readonly<Props>) => {
    const { t } = useTranslation();
    const mode = usePriceEntryMode();
    const ratePercent = rate.ratePercent;
    const entersNet = mode === 'net' && ratePercent !== null;

    // The typed net is kept as typed: re-deriving it from the converted gross
    // on every keystroke would rewrite "10." to "10" under the cursor.
    const [netText, setNetText] = useState(() =>
        (ratePercent === null ? '' : convert(gross, (g) => netFromGross(g, ratePercent))));
    const emitted = useRef<string | null>(null);
    const previousRate = useRef<number | null>(ratePercent);

    useEffect(() => {
        const rateBefore = previousRate.current;
        previousRate.current = ratePercent;
        if (!entersNet) return;
        // The operator picked another tax category for this item: the net they
        // typed stays, and the gross follows the new rate.
        if (rateBefore !== null && rateBefore !== ratePercent && isAmount(netText)) {
            const next = convert(netText, (n) => grossFromNet(n, ratePercent));
            emitted.current = next;
            onGrossChange(next);
            return;
        }
        // The gross changed from outside (first load, a reset): show its net.
        // Our own emission coming back is not a change — that would rewrite
        // what the operator is typing.
        if (sameAmount(gross, emitted.current)) return;
        emitted.current = null;
        const derived = convert(gross, (g) => netFromGross(g, ratePercent));
        if (derived !== netText) setNetText(derived);
    }, [gross, ratePercent, entersNet, netText, onGrossChange]);

    const handleNet = (text: string) => {
        if (ratePercent === null) return;
        setNetText(text);
        const next = convert(text, (n) => grossFromNet(n, ratePercent));
        emitted.current = next;
        onGrossChange(next);
    };

    const netLabel = (base: string) => t('price_entry.net_label', { label: base });

    let note: string | null = null;
    if (entersNet && isAmount(gross)) {
        note = t('price_entry.gross_line', {
            amount: formatCurrency(Number(gross), getCurrentLang()),
            rate: formatRatePercent(ratePercent),
        });
    } else if (mode === 'net' && rate.block) {
        note = t(`price_entry.blocked.${rate.block}`);
    }

    return (
        // A plain block, not a flex column: inside a right-aligned table cell
        // the input keeps following the cell's text alignment.
        <div>
            {children(entersNet
                ? { value: netText, onChange: handleNet, entersNet, label: netLabel }
                : { value: gross, onChange: onGrossChange, entersNet, label: (base) => base })}
            {note && <p className={'mt-1 text-xs text-foreground/60'}>{note}</p>}
        </div>
    );
};

export default NetGrossField;
